import type { PageChangeFn } from '../../../components/Pagination/Pagination';
import type {
  ContentObject,
  ContentTypeDefinition,
} from '../../../lib/flotiq-client/types/api-response';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getObjectTitle,
  getTestProps,
  strToChecksum,
} from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import DataGrid from '../../../components/DataGrid/DataGrid';
import DateContainer from '../../../components/DateContainer/DateContainer';
import LinkButton from '../../../components/LinkButton/LinkButton';
import Loader from '../../../components/Loader/Loader';
import StatusBar from '../../../components/StatusBar/StatusBar';
import StatusPill from '../../../components/StatusPill/StatusPill';

import { useContentTypes, useSearch } from '../../../hooks/api';
import { useImportSpace } from '../../../hooks/useImportSpace';
import useSelectedSpace from '../../../hooks/useSelectedSpace';

import { CaretRightIcon, PlusIcon } from '../../../images/shapes';

import { usePopulateSpaceModal } from '../../../layout/PopulateSpaceModal/usePopulateSpaceModal';

const CONTENT_TYPE_COLORS = [
  'lime' as const,
  'blue' as const,
  'red' as const,
  'orange' as const,
];

const CTD_PARAMS = { limit: 1000 };
const LAST_CHANGES_LIMIT = 10;

const DashboardLatestUpdates = ({ testId }: { testId?: string }) => {
  const { t } = useTranslation();
  const { buildUrlWithSpace } = useSelectedSpace();
  const { openPopulateSpaceModal } = usePopulateSpaceModal();
  const { openImportModal } = useImportSpace();

  const [currentPage, setCurrentPage] = useState(1);

  const { data: contentTypes, isLoading: contentTypesLoading } =
    useContentTypes(CTD_PARAMS);

  const searchParams = useMemo(
    () => ({
      q: '*',
      limit: LAST_CHANGES_LIMIT,
      order_by: 'internal.updatedAt',
      order_direction: 'desc',
      page: currentPage,
    }),
    [currentPage],
  );

  const {
    data: searchData,
    isLoading: searchLoading,
    pagination,
    reload,
  } = useSearch(searchParams);

  const handlePageChange: PageChangeFn = (page) => {
    setCurrentPage(page);
  };

  const contentTypesDictionary = contentTypes.reduce(
    (acc, ctd) => {
      acc[ctd.name] = ctd;
      return acc;
    },
    {} as Record<string, ContentTypeDefinition>,
  );

  const getCtdLink = (contentType: ContentTypeDefinition) => {
    if (!contentType || (contentType.internal && contentType.name !== '_media'))
      return '';
    return contentType.name === '_media'
      ? buildUrlWithSpace('media')
      : buildUrlWithSpace(`content-type-objects/${contentType.name}`);
  };

  const latestObjects = searchData.map((element) => {
    const contentTypeDefinition =
      contentTypesDictionary[element.item.internal.contentType];

    return {
      status: element.item.internal.status,
      title: getObjectTitle(element.item, contentTypeDefinition),
      contentType: {
        name: element.item.internal.contentType,
        label:
          contentTypeDefinition?.label || element.item.internal.contentType,
        link: getCtdLink(contentTypeDefinition),
      },
      updatedAt: element.item.internal.updatedAt,
      link: buildUrlWithSpace(
        element.item.internal.contentType === '_media'
          ? `media/edit/${element.item.id}`
          : `content-type-objects/edit/${element.item.internal.contentType}/${element.item.id}`,
      ),
    };
  });

  const columns = [
    {
      accessor: 'status',
      label: t('Global.Status'),
      width: 120,
      flexGrow: 0,
      render: (status: ContentObject['internal']['status']) => (
        <StatusPill
          containerClasses="inline-flex items-center justify-center"
          color={status}
          status={t(`ObjectStatus.Status.${status}`)}
          size="sm"
        />
      ),
      sortable: false,
    },
    {
      accessor: 'title',
      label: t('Global.ObjectTitle'),
      minWidth: 140,
      render: (title: string) => (
        <span className="truncate min-w-0">{title}</span>
      ),
      sortable: false,
    },
    {
      accessor: 'contentType',
      label: t('Global.ContentType'),
      minWidth: 140,
      render: (data: (typeof latestObjects)[number]['contentType']) => (
        <StatusPill
          status={data.label}
          color={CONTENT_TYPE_COLORS[strToChecksum(data.name) % 4]}
          containerClasses={
            'flex items-center h-full text-xs lg:text-sm h-6 lg:h-7 ' +
            'group-hover:underline group-hover:shadow-lg hover:underline'
          }
          {...getTestProps(testId, `${data.name}-ctd`, 'testId')}
        />
      ),
      sortable: false,
    },
    {
      accessor: 'updatedAt',
      label: t('Global.UpdatedAt'),
      width: 160,
      flexGrow: 0,
      render: (data: string) => <DateContainer date={data} isBold={false} />,
      sortable: false,
    },
  ];

  const getRowLinkOptions = (
    accessor: string,
    rowData: (typeof latestObjects)[number],
  ) =>
    accessor === 'contentType'
      ? { to: rowData.contentType.link }
      : { to: rowData.link };

  return (
    <section>
      <h2 className="text-xl dark:text-white">
        {t('Global.YourLatestUpdates')}
      </h2>

      <div className="mt-2 overflow-hidden rounded-lg bg-white dark:bg-slate-950">
        <div
          id="latest_updates"
          className="h-[calc(100%-40px)] md:h-[calc(100%-72px)]"
        >
          <DataGrid
            columns={columns}
            data={latestObjects}
            isLoading={searchLoading || contentTypesLoading}
            statusBar={
              <StatusBar
                rows={pagination.count}
                currentPage={currentPage}
                pagesCount={pagination.total_pages}
                handlePageChange={handlePageChange}
                handleDataUpdate={reload}
                resultsFrom={
                  (pagination.current_page - 1) * LAST_CHANGES_LIMIT + 1
                }
                resultsTo={
                  (pagination.current_page - 1) * LAST_CHANGES_LIMIT +
                  pagination.count
                }
                resultsTotalCount={pagination.total_count}
              />
            }
            noDataInfo={
              <div className="px-5 w-full h-full flex flex-col gap-6 items-center justify-center">
                <p className="text-center text-lg dark:text-white">
                  <strong>{t('ObjectsOfType.NoMatchingData')}</strong>
                  <br />
                  <span className="font-normal">
                    {t('Global.CreateYourProjectStructure')}
                  </span>
                </p>

                <div className="flex flex-wrap justify-center gap-4 lg:gap-8">
                  <LinkButton
                    link={buildUrlWithSpace('content-type-definitions')}
                    buttonColor="borderlessBlue"
                    additionalClasses="hover:opacity-100 hover:underline"
                    iconImage={<PlusIcon className="size-2.5" />}
                  >
                    {t('Home.CreateContentType')}
                  </LinkButton>
                  <Button
                    onClick={openPopulateSpaceModal}
                    buttonColor="borderlessBlue"
                    additionalClasses="hover:opacity-100"
                    iconImage={<CaretRightIcon className="size-2.5" />}
                  >
                    {t('Home.TryDemo')}
                  </Button>
                  <Button
                    buttonColor="borderlessBlue"
                    additionalClasses="hover:opacity-100"
                    iconImage={<CaretRightIcon className="size-2.5" />}
                    onClick={openImportModal}
                  >
                    {t('Home.ImportData')}
                  </Button>
                </div>
              </div>
            }
            loadingIcon={<Loader size="small" type="spinner-grid" />}
            getRowLinkOptions={getRowLinkOptions}
            tableHeight={250}
            {...getTestProps(testId, 'grid', 'testId')}
          />
        </div>
      </div>
    </section>
  );
};

export default DashboardLatestUpdates;
